import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Button, Card, Input, Select, Empty, Spinner, Badge } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { Plus, Pencil, Trash2, X, Search } from "lucide-react";

const empty = {
  nome: "", tipo: "PJ", cnpj_cpf: "", ie: "", indicador_ie: "9",
  email: "", telefone: "",
  logradouro: "", numero: "", complemento: "", bairro: "",
  cidade: "", uf: "", cep: "", codigo_municipio_ibge: "",
  consumidor_final: true, prazo_faturamento_dias: 0, observacoes: "",
};

export default function Clients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/clientes"); setItems(data); }
    catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((c) =>
      (c.nome || "").toLowerCase().includes(s) ||
      (c.cnpj_cpf || "").toLowerCase().includes(s) ||
      (c.cidade || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); setErr(""); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); setErr(""); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = { ...form, consumidor_final: !!form.consumidor_final, prazo_faturamento_dias: parseInt(form.prazo_faturamento_dias || 0, 10) || 0 };
      if (editing) await api.put(`/clientes/${editing.id}`, payload);
      else await api.post("/clientes", payload);
      setOpen(false); await load();
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Excluir cliente?")) return;
    try { await api.delete(`/clientes/${id}`); await load(); }
    catch (e) { alert(formatErr(e)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <Layout
      title="Clientes"
      subtitle="Cadastro fiscal para emissão de NF-e"
      actions={<Button onClick={openNew} data-testid="new-client-button"><Plus size={16} /> Novo Cliente</Button>}
    >
      <Card className="p-4 mb-5">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Nome, CNPJ/CPF ou cidade..."
            className="w-full rounded-md bg-stone-50 border border-stone-300 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
            data-testid="client-search-input"
          />
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner size={28} /></div>
      ) : filtered.length === 0 ? (
        <Card><Empty title="Nenhum cliente encontrado" hint="Cadastre seu primeiro cliente." /></Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-ink-200">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">CNPJ/CPF</th>
                <th className="px-4 py-3">Cidade/UF</th>
                <th className="px-4 py-3">Telefone</th>
                <th className="px-4 py-3 w-1">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-ink-200 last:border-0 hover:bg-stone-50 transition-colors" data-testid={`client-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium text-ink-900">{c.nome}</td>
                  <td className="px-4 py-3"><Badge tone={c.tipo === "PJ" ? "success" : "neutral"}>{c.tipo}</Badge></td>
                  <td className="px-4 py-3 font-mono text-ink-700">{c.cnpj_cpf || "—"}</td>
                  <td className="px-4 py-3 text-ink-700">{[c.cidade, c.uf].filter(Boolean).join("/") || "—"}</td>
                  <td className="px-4 py-3 text-ink-700">{c.telefone || "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button onClick={() => openEdit(c)} className="p-1.5 rounded-md hover:bg-stone-100 text-ink-700" data-testid={`client-edit-${c.id}`}><Pencil size={16} /></button>
                    <button onClick={() => onDelete(c.id)} className="p-1.5 rounded-md hover:bg-red-100 text-danger" data-testid={`client-delete-${c.id}`}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200 sticky top-0 bg-white">
              <h3 className="font-heading font-bold text-ink-900">{editing ? "Editar Cliente" : "Novo Cliente"}</h3>
              <button onClick={() => setOpen(false)} className="text-ink-500 hover:text-ink-900"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2"><Input label="Nome / Razão Social" required value={form.nome} onChange={set("nome")} data-testid="client-nome" /></div>
                <Select label="Tipo" value={form.tipo} onChange={set("tipo")} data-testid="client-tipo">
                  <option value="PJ">PJ — Jurídica</option>
                  <option value="PF">PF — Física</option>
                </Select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input label={form.tipo === "PJ" ? "CNPJ" : "CPF"} value={form.cnpj_cpf} onChange={set("cnpj_cpf")} data-testid="client-cnpj-cpf" />
                <Input label="Inscrição Estadual" value={form.ie} onChange={set("ie")} />
                <Select label="Indicador IE" value={form.indicador_ie} onChange={set("indicador_ie")}>
                  <option value="1">1 — Contribuinte</option>
                  <option value="2">2 — Isento</option>
                  <option value="9">9 — Não contribuinte</option>
                </Select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="E-mail" type="email" value={form.email} onChange={set("email")} />
                <Input label="Telefone" value={form.telefone} onChange={set("telefone")} />
              </div>

              <div className="pt-3 border-t border-ink-200">
                <div className="text-xs font-bold uppercase tracking-wider text-ink-500 mb-3">Endereço</div>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                  <div className="md:col-span-4"><Input label="Logradouro" value={form.logradouro} onChange={set("logradouro")} /></div>
                  <Input label="Número" value={form.numero} onChange={set("numero")} />
                  <Input label="Complemento" value={form.complemento} onChange={set("complemento")} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mt-3">
                  <div className="md:col-span-2"><Input label="Bairro" value={form.bairro} onChange={set("bairro")} /></div>
                  <div className="md:col-span-2"><Input label="Cidade" value={form.cidade} onChange={set("cidade")} /></div>
                  <Input label="UF" maxLength={2} value={form.uf} onChange={set("uf")} />
                  <Input label="CEP" value={form.cep} onChange={set("cep")} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <div>
                    <Input
                      label="Cód. Município (IBGE) — 7 dígitos"
                      value={form.codigo_municipio_ibge}
                      onChange={set("codigo_municipio_ibge")}
                      maxLength={7}
                      placeholder="ex.: 3556453"
                      data-testid="client-ibge"
                    />
                    <p className="text-[11px] text-ink-500 mt-1">
                      Obrigatório para NF-e. Consulte em{" "}
                      <a href="https://www.ibge.gov.br/explica/codigos-dos-municipios.php" target="_blank" rel="noreferrer" className="underline text-brand-600">ibge.gov.br</a>
                    </p>
                  </div>
                  <label className="flex items-center gap-2 mt-6">
                    <input type="checkbox" checked={!!form.consumidor_final} onChange={set("consumidor_final")} className="accent-brand-500" />
                    <span className="text-sm">Consumidor final</span>
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Prazo de faturamento (dias)"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Ex.: 30"
                  value={form.prazo_faturamento_dias ?? 0}
                  onChange={set("prazo_faturamento_dias")}
                  data-testid="client-prazo"
                />
                <div className="text-xs text-ink-500 flex items-center pt-6">
                  Prazo padrão para vencimento após a saída. Pode ser alterado a cada venda.
                </div>
              </div>
              <Input label="Observações" value={form.observacoes} onChange={set("observacoes")} />

              {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={busy} data-testid="client-submit">{busy ? <Spinner size={16} /> : "Salvar"}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
